import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { fetchQueue, fetchStartedToday, subscribeToQueue } from '../services/queue';
import type { QueueEntry, QueueOrderPolicy, StartedEntry } from '../types/database';

interface Ctx {
  /** Live queue at the current location, ordered by visual position. Parked
   *  ("even weg") rows stay in place with parked=true. */
  entries: QueueEntry[];
  /** position_only (pure check-in order, default) or slot_first. */
  orderPolicy: QueueOrderPolicy;
  /** ETA inputs the RPC used (informational). */
  stations: number;
  cycleMinutes: number;
  /** First NON-PARKED entry — the visitor a launch pops by default. */
  headEntry: QueueEntry | null;
  /** null = head; otherwise the queue_entry_id chosen via QueuePickerModal. */
  selectedDequeueEntryId: string | null;
  /** Resolved selection — selected entry if any, else head. */
  selectedEntry: QueueEntry | null;
  /** When true (default), a successful experience launch dequeues the selected entry. */
  dequeueOnLaunch: boolean;
  setSelected: (queueEntryId: string | null) => void;
  setDequeueOnLaunch: (v: boolean) => void;
  /** Force a refetch (e.g. after a manual dequeue). */
  refresh: () => void;
  /** Loading state for first fetch. */
  loading: boolean;
  /** Today's started rows at the location, newest event first. */
  startedToday: StartedEntry[];
  /** Newest started row per PC (infra.pc_instances.id) — "who plays here". */
  startedByPc: ReadonlyMap<string, StartedEntry>;
  /** The started row with the most recent event: what fn_undo_last_dequeue
   *  would most likely restore. Age check is the caller's (5-minute window). */
  latestDequeue: StartedEntry | null;
}

const QueueDequeueContext = createContext<Ctx | null>(null);

interface ProviderProps {
  /** Pulled from the active room's `location` field (infra.rooms.location). */
  locationId: string | null;
  children: ReactNode;
}

export function QueueDequeueProvider({ locationId, children }: ProviderProps) {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [orderPolicy, setOrderPolicy] = useState<QueueOrderPolicy>('position_only');
  const [stations, setStations] = useState(1);
  const [cycleMinutes, setCycleMinutes] = useState(25);
  const [startedToday, setStartedToday] = useState<StartedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDequeueEntryId, setSelected] = useState<string | null>(null);
  const [dequeueOnLaunch, setDequeueOnLaunch] = useState(true);

  const load = useCallback(async () => {
    if (!locationId) {
      setEntries([]);
      setStartedToday([]);
      setLoading(false);
      return;
    }
    // Queue + started-today together: one realtime event (a dequeue moves a
    // row from one list to the other) refreshes both, and a failure of one
    // never blanks the other. Don't blow away the previous snapshot on a
    // transient fetch failure — the operator keeps the last good view.
    const [q, s] = await Promise.allSettled([fetchQueue(locationId), fetchStartedToday(locationId)]);
    if (q.status === 'fulfilled') {
      setEntries(q.value.entries);
      setOrderPolicy(q.value.orderPolicy);
      setStations(q.value.stations);
      setCycleMinutes(q.value.cycleMinutes);
    } else {
      console.error('fetchQueue failed', q.reason);
    }
    if (s.status === 'fulfilled') {
      setStartedToday(s.value);
    } else {
      console.error('fetchStartedToday failed', s.reason);
    }
    setLoading(false);
  }, [locationId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (!locationId) return;
    // Debounce a burst of postgres_changes events (one per-person enqueue
    // inserts N rows in one txn → N events) into a single refetch.
    let t: ReturnType<typeof setTimeout> | null = null;
    const unsub = subscribeToQueue(locationId, () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { void load(); }, 300);
    });
    return () => {
      if (t) clearTimeout(t);
      unsub();
    };
  }, [locationId, load]);

  // 60s safety poll for websocket gaps.
  useEffect(() => {
    if (!locationId) return;
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [locationId, load]);

  // Clear the override when the selected entry leaves the queue (got
  // dequeued by another operator, removed from admin, etc.).
  useEffect(() => {
    if (
      selectedDequeueEntryId &&
      !entries.some((e) => e.queue_entry_id === selectedDequeueEntryId)
    ) {
      setSelected(null);
    }
  }, [entries, selectedDequeueEntryId]);

  const headEntry = useMemo(() => entries.find((e) => !e.parked) ?? null, [entries]);
  const selectedEntry = useMemo(() => {
    if (selectedDequeueEntryId) {
      return entries.find((e) => e.queue_entry_id === selectedDequeueEntryId) ?? null;
    }
    return headEntry;
  }, [entries, selectedDequeueEntryId, headEntry]);

  // startedToday is newest-first, so the first row seen per PC wins.
  const startedByPc = useMemo(() => {
    const m = new Map<string, StartedEntry>();
    for (const s of startedToday) {
      if (s.started_pc_id && !m.has(s.started_pc_id)) m.set(s.started_pc_id, s);
    }
    return m;
  }, [startedToday]);
  const latestDequeue = startedToday[0] ?? null;

  const value: Ctx = {
    entries,
    orderPolicy,
    stations,
    cycleMinutes,
    headEntry,
    selectedDequeueEntryId,
    selectedEntry,
    dequeueOnLaunch,
    setSelected,
    setDequeueOnLaunch,
    refresh: () => void load(),
    loading,
    startedToday,
    startedByPc,
    latestDequeue,
  };

  return (
    <QueueDequeueContext.Provider value={value}>
      {children}
    </QueueDequeueContext.Provider>
  );
}

export function useQueueDequeue(): Ctx {
  const v = useContext(QueueDequeueContext);
  if (!v) {
    throw new Error('useQueueDequeue must be used inside <QueueDequeueProvider>');
  }
  return v;
}

// Optional accessor that returns null when there's no provider — useful for
// PCTile, which renders on both auth-bypassed pages (QueueDisplay) and the
// normal Dashboard.
export function useQueueDequeueOptional(): Ctx | null {
  return useContext(QueueDequeueContext);
}
